import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LanguageSelector, { useLanguage } from '../../components/feature/LanguageSelector';
import { translations } from '../../mocks/translations';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import TripAdvisorButton from '../../components/feature/TripAdvisorButton';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function AboutPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const language = useLanguage();
  const t = translations[language];

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  useSEO({
    title: 'Sobre Nosotros - Leury Tours | Más de 10 Años de Experiencia en Puerto Plata',
    description: 'Conoce a Leury Marte, fundador de Leury Tours. Más de 10 años compartiendo la belleza de Puerto Plata con viajeros de todo el mundo. Servicio profesional, seguro y personalizado.',
    keywords: 'Leury Tours, sobre nosotros, tours Puerto Plata, guía turístico Puerto Plata, experiencia turística República Dominicana',
    ogImage: 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/1318a15b29376172971e214316561a5d.jpeg',
    schema: generateWebPageSchema({
      name: 'Sobre Nosotros - Leury Tours',
      description: 'Conoce la historia de Leury Tours y nuestro compromiso con la excelencia en servicios turísticos en Puerto Plata',
      url: `${siteUrl}/about`
    })
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const milestones = [
    {
      year: '2014',
      title: language === 'en' ? 'The Beginning' : 'El Comienzo',
      description: language === 'en' 
        ? 'Started with a single vehicle and a passion for sharing the beauty of Puerto Plata with visitors from around the world.'
        : 'Comenzamos con un solo vehículo y la pasión de compartir la belleza de Puerto Plata con visitantes de todo el mundo.'
    },
    {
      year: '2017',
      title: language === 'en' ? 'Growing Fleet' : 'Flota en Crecimiento',
      description: language === 'en'
        ? 'Expanded our fleet to include multiple vehicles and hired our first team of certified bilingual guides.'
        : 'Expandimos nuestra flota para incluir múltiples vehículos y contratamos nuestro primer equipo de guías bilingües certificados.'
    },
    {
      year: '2020',
      title: language === 'en' ? 'TripAdvisor Excellence' : 'Excelencia en TripAdvisor',
      description: language === 'en'
        ? 'Achieved Certificate of Excellence on TripAdvisor with over 500 five-star reviews from satisfied customers.'
        : 'Logramos el Certificado de Excelencia en TripAdvisor con más de 500 reseñas de cinco estrellas de clientes satisfechos.'
    },
    {
      year: '2024',
      title: language === 'en' ? '10+ Years of Excellence' : '10+ Años de Excelencia',
      description: language === 'en'
        ? 'Celebrating over a decade of creating unforgettable experiences for thousands of travelers visiting the Dominican Republic.'
        : 'Celebrando más de una década creando experiencias inolvidables para miles de viajeros que visitan la República Dominicana.'
    }
  ];

  const values = [
    {
      icon: 'ri-heart-3-line',
      title: language === 'en' ? 'Passion' : 'Pasión',
      description: language === 'en'
        ? 'We love what we do and it shows in every tour we lead and every guest we serve.'
        : 'Amamos lo que hacemos y se nota en cada tour que guiamos y cada huésped que atendemos.'
    },
    {
      icon: 'ri-shield-check-line',
      title: language === 'en' ? 'Safety' : 'Seguridad',
      description: language === 'en'
        ? 'Your safety is our top priority. All our vehicles are insured and our guides are certified.'
        : 'Tu seguridad es nuestra prioridad. Todos nuestros vehículos están asegurados y nuestros guías certificados.'
    },
    {
      icon: 'ri-user-heart-line',
      title: language === 'en' ? 'Personal Touch' : 'Toque Personal',
      description: language === 'en'
        ? 'We treat every guest like family, ensuring personalized attention and memorable experiences.'
        : 'Tratamos a cada huésped como familia, asegurando atención personalizada y experiencias memorables.'
    },
    {
      icon: 'ri-star-line',
      title: language === 'en' ? 'Excellence' : 'Excelencia',
      description: language === 'en'
        ? 'We strive for excellence in everything we do, from customer service to tour quality.'
        : 'Nos esforzamos por la excelencia en todo lo que hacemos, desde el servicio al cliente hasta la calidad del tour.'
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* WhatsApp & TripAdvisor Buttons */}
      <WhatsAppButton />
      <TripAdvisorButton />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg"
                alt="Leury Tours"
                className="h-10 sm:h-12 w-auto"
              />
              <span className={`text-lg sm:text-xl font-bold transition-colors ${scrolled ? 'text-[#0A2463]' : 'text-white'}`}>Leury Tours</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link to="/" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>
                {t.nav.home}
              </Link>
              <Link to="/about" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#00B4D8]' : 'text-[#48CAE4]'}`}>
                {t.nav.about}
              </Link>
              <Link to="/tours" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>
                {t.nav.tours}
              </Link>
              <Link to="/transfers" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>
                {t.nav.transfers}
              </Link>
              <a href="/#reviews" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>
                {t.nav.reviews}
              </a>
              <a href="/#contact" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>
                {t.nav.contact}
              </a>
              <LanguageSelector scrolled={scrolled} />
              <a
                href="/#booking"
                className="bg-[#FF6B6B] text-white px-5 xl:px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#ff5252] transition-all whitespace-nowrap"
              >
                {t.nav.bookNow}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 lg:hidden">
              <LanguageSelector scrolled={scrolled} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-lg ${scrolled ? 'text-[#0A2463]' : 'text-white'}`}
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[60px] bg-white z-40 overflow-y-auto">
            <div className="px-4 py-6 space-y-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.home}
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#00B4D8] py-3 border-b border-gray-100"
              >
                {t.nav.about}
              </Link>
              <Link
                to="/tours"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.tours}
              </Link>
              <Link
                to="/transfers"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.transfers}
              </Link>
              <a
                href="/#reviews"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.reviews}
              </a>
              <a
                href="/#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100"
              >
                {t.nav.contact}
              </a>
              <a
                href="/#booking"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full bg-[#FF6B6B] text-white px-6 py-3 rounded-full text-base font-semibold text-center mt-6"
              >
                {t.nav.bookNow}
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[500px] sm:h-[550px] lg:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/bdbc7907756451b2e0db0008c3367cb5.jpeg"
            alt="About Leury Tours"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2463]/90 via-[#0A2463]/70 to-[#0A2463]/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full text-center">
          <span className="inline-block text-[#48CAE4] text-xs sm:text-sm font-bold tracking-wider uppercase mb-3 sm:mb-4">
            {language === 'en' ? 'Our Story' : 'Nuestra Historia'}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-4 sm:mb-6">
            {t.nav.about}
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto">
            {language === 'en' 
              ? 'More than 10 years creating unforgettable experiences in Puerto Plata'
              : 'Más de 10 años creando experiencias inolvidables en Puerto Plata'}
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                <div className="aspect-[4/5] w-full">
                  <img 
                    src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/1318a15b29376172971e214316561a5d.jpeg" 
                    alt="Leury - Founder of Leury Tours" 
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 sm:w-40 sm:h-40 bg-[#FF6B6B]/10 rounded-full -z-10"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 sm:w-32 sm:h-32 bg-[#00B4D8]/10 rounded-full -z-10"></div>
            </div>

            {/* Content */}
            <div>
              <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase">
                {language === 'en' ? 'Meet me' : 'Conoce al Fundador'}
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mt-2 sm:mt-3 mb-4 sm:mb-6">
                Leury Marte
              </h2>
              <div className="space-y-4 sm:space-y-5 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  {language === 'en'
                    ? "Hello! I'm Leury Marte, founder and owner of Leury Tours. Born and raised in the beautiful province of Puerto Plata, I've dedicated more than 10 years of my life to sharing the wonders of my homeland with visitors from around the world."
                    : "¡Hola! Soy Leury, fundador y propietario de Leury Tours. Nacido y criado en la hermosa provincia de Puerto Plata, he dedicado más de 10 años de mi vida a compartir las maravillas de mi tierra natal con visitantes de todo el mundo."}
                </p>
                <p>
                  {language === 'en'
                    ? "My journey in the tourism industry began with a simple dream: to show travelers the authentic Dominican Republic beyond the resort."
                    : "Mi viaje en la industria del turismo comenzó con un sueño simple: mostrar a los viajeros la auténtica República Dominicana más allá de las paredes del resort. Lo que comenzó como una pequeña operación con un vehículo ha crecido hasta convertirse en una empresa de tours de confianza que atiende a miles de clientes felices cada año."}
                </p>
                <p>
                  {language === 'en'
                    ? "With extensive experience in customer service and a deep knowledge of every corner of Puerto Plata, I personally ensure that each guest receives the highest quality experience. Whether you're jumping through the waterfalls of Damajagua, exploring the historic streets of our colonial city, or meeting playful monkeys at the sanctuary, I guarantee you'll leave with memories that last a lifetime."
                    : "Con amplia experiencia en servicio al cliente y un profundo conocimiento de cada rincón de Puerto Plata, personalmente me aseguro de que cada huésped reciba la experiencia de más alta calidad. Ya sea que estés saltando por las 27 cascadas de Damajagua, explorando las calles históricas de nuestra ciudad colonial, o conociendo a los juguetones monos en el santuario, te garantizo que te irás con recuerdos que durarán toda la vida."}
                </p>
                <p>
                  {language === 'en'
                    ? "I'm fluent in both English and Spanish, and I take pride in providing personalized attention to every guest. For me, this isn't just a business – it's my passion and my way of sharing the beauty, culture, and warmth of the Dominican people with the world."
                    : "Hablo con fluidez tanto inglés como español, y me enorgullezco de brindar atención personalizada a cada huésped. Para mí, esto no es solo un negocio – es mi pasión y mi forma de compartir la belleza, cultura y calidez del pueblo dominicano con el mundo."}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-10">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#00B4D8]">10+</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    {language === 'en' ? 'Years Experience' : 'Años de Experiencia'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#FF6B6B]">500+</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    {language === 'en' ? 'Happy Guests' : 'Huéspedes Felices'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#48CAE4]">5.0</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    {language === 'en' ? 'Star Rating' : 'Calificación'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-[#FFF9F0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase">
              {language === 'en' ? 'What We Stand For' : 'Lo Que Representamos'}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A2463] mt-2 sm:mt-3 mb-3 sm:mb-4">
              {language === 'en' ? 'Our Core Values' : 'Nuestros Valores'}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
              {language === 'en'
                ? 'These principles guide everything we do at Leury Tours'
                : 'Estos principios guían todo lo que hacemos en Leury Tours'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all text-center group">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#00B4D8]/20 to-[#48CAE4]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5 group-hover:scale-110 transition-transform">
                  <i className={`${value.icon} text-[#00B4D8] text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#0A2463] mb-2 sm:mb-3">{value.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#0A2463] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30 0 13.431 13.431 0 30 0z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
            }}
          ></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            {language === 'en' ? "Ready to Explore Puerto Plata?" : "¿Listo para Explorar Puerto Plata?"}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8">
            {language === 'en'
              ? "Let me personally show you the best of the Dominican Republic. Book your adventure today!"
              : "Déjame mostrarte personalmente lo mejor de la República Dominicana. ¡Reserva tu aventura hoy!"}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              className="inline-block bg-[#FF6B6B] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-bold text-sm sm:text-base hover:bg-[#ff5252] transition-all whitespace-nowrap"
            >
              {language === 'en' ? 'Explore Our Tours' : 'Explorar Nuestros Tours'}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2463] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <img
                  src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg"
                  alt="Leury Tours"
                  className="h-8 sm:h-10 w-auto"
                />
                <span className="text-lg sm:text-xl font-bold">Leury Tours</span>
              </div>
              <p className="text-white/70 text-xs sm:text-sm mb-4 sm:mb-6">
                {t.home?.heroSubtitle || "Puerto Plata's Premier Tour Experience"}
              </p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm">+1 (809)-461-9001</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-mail-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm break-all">leury2124@gmail.com</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">
                {t.footer.explore}
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link to="/about" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">
                    {t.nav.about}
                  </Link>
                </li>
                <li>
                  <Link to="/transfers" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">
                    {t.footer.transfers}
                  </Link>
                </li>
                <li>
                  <Link to="/tours" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">
                    {t.footer.tours}
                  </Link>
                </li>
                <li>
                  <a href="/#contact" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">
                    {t.footer.customServices}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">
                {t.footer.followUs}
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="https://www.instagram.com/leury2124?igsh=bHdwNHcxbmVtOQ%3D%3D&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    Instagram
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.facebook.com/share/1VPJPAepMd/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    Facebook
                  </a>
                </li>
                <li>
                  <a
                    href="https://tripadvisor.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors"
                  >
                    TripAdvisor
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <a
                href="/#booking"
                className="block w-full bg-[#FF6B6B] text-white py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm hover:bg-[#ff5252] transition-all text-center mb-3 sm:mb-4 whitespace-nowrap"
              >
                {t.nav.bookNow}
              </a>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-white/60 text-xs sm:text-sm text-center sm:text-left">
                {t.footer.allRightsReserved}
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link
                  to="/privacy"
                  className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors"
                >
                  {t.footer.privacyPolicy}
                </Link>
                <Link
                  to="/terms"
                  className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors"
                >
                  {t.footer.termsConditions}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
